import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin } from "lucide-react";
import { MaskedLines } from "@/components/site/Reveal";
import { IMG } from "@/components/site/content";
import { useLang } from "@/context/LanguageContext";

export default function Hero({ onCta }) {
  const { t } = useLang();
  const h = t.hero;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBig = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const ySmall = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 8]);

  return (
    <section
      id="hero"
      ref={ref}
      className="relative flex min-h-[100svh] items-center overflow-hidden bg-[#F6F1E8] pt-24"
      data-testid="section-hero"
    >
      <div className="pointer-events-none absolute -right-40 top-1/4 h-96 w-96 rounded-full bg-[#3F9BA6]/20 blur-3xl" />

      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-10 px-5 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="mb-5 inline-flex items-center gap-2 rounded-full border-2 border-[#14283A] px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-[#14283A]"
          >
            <span className="grad-bar h-2 w-2 rounded-full" />
            {h.badge}
          </motion.div>

          <h1 className="font-display text-5xl leading-[0.92] text-[#14283A] sm:text-7xl lg:text-8xl">
            <MaskedLines lines={h.lines} />
            <span className="mt-1 block overflow-hidden">
              <motion.span
                className="grad-text block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.85, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
              >
                {h.accent}
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="mt-6 max-w-md text-base text-[#14283A]/80 sm:text-lg"
            data-testid="hero-subtitle"
          >
            {h.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={onCta}
              data-testid="hero-cta-button"
              className="grad-bar rounded-full px-8 py-4 text-base font-bold text-white shadow-lg transition-transform duration-200 hover:scale-105 active:scale-95"
            >
              {h.cta}
            </button>
            <a
              href="#concept"
              className="flex items-center gap-2 text-sm font-bold text-[#14283A] transition-colors hover:text-[#EA7A4F]"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-[#14283A]">
                <ArrowDown className="h-4 w-4" />
              </span>
              {h.secondary}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1 }}
            className="mt-6 flex items-center gap-2 text-xs text-[#14283A]/60"
          >
            <MapPin className="h-4 w-4" />
            {h.location}
          </motion.div>
        </div>

        <div className="relative h-[380px] sm:h-[520px]">
          <motion.figure
            style={{ y: yBig, rotate }}
            className="absolute right-2 top-2 w-[62%] bg-white p-3 pb-8 shadow-2xl"
          >
            <span className="tape -top-3 left-1/2 -translate-x-1/2 -rotate-2" aria-hidden="true" />
            <img
              src={IMG.heroVibe}
              alt=""
              className="aspect-[4/5] w-full object-cover"
              fetchPriority="high"
            />
            <figcaption className="font-marker absolute bottom-2 left-0 right-0 text-center text-[#14283A]">
              {h.capBig}
            </figcaption>
          </motion.figure>

          <motion.figure
            style={{ y: ySmall }}
            className="absolute bottom-0 left-0 w-[46%] rotate-[-6deg] bg-white p-2.5 pb-7 shadow-2xl"
          >
            <img src={IMG.nightlife} alt="" className="aspect-square w-full object-cover" />
            <figcaption className="font-marker absolute bottom-1.5 left-0 right-0 text-center text-sm text-[#14283A]">
              {h.capSmall}
            </figcaption>
          </motion.figure>
        </div>
      </div>
    </section>
  );
}
